import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '@env/environment';
import {
  AtualizacaoDisponivelData, AtualizacaoDisponivelSnackbarComponent,
} from '@core/pwa/atualizacao-disponivel-snackbar.component';
import { AtualizacaoObrigatoriaService } from '@core/pwa/atualizacao-obrigatoria.service';
import { recarregarComVersaoNova } from '@core/pwa/recarregar-app';
import { compararVersoes } from '@core/pwa/versao.utils';

/** Quanto tempo o aviso fica calado depois de um "Depois". */
const SILENCIO_MS = 60 * 60 * 1000;

/** Onde o adiamento é guardado: por aba, e some quando ela fecha. */
const CHAVE_ADIAMENTO = 'fury.atualizacao.adiada';

/** Marca usada quando quem avisou foi o service worker, que não conhece o número da versão. */
const SEM_VERSAO = '(build)';

interface Adiamento {
  versao: string;
  ate: number;
}

/**
 * Aviso de que existe versão nova, quando atualizar ainda é opcional.
 *
 * <p>É o degrau de baixo do mesmo mecanismo do {@link AtualizacaoObrigatoriaService}: a versão em
 * uso continua conversando com a API, então não há motivo para interromper ninguém — só para
 * contar que há algo melhor e deixar a escolha com quem está trabalhando.</p>
 *
 * <p>Duas entradas alimentam o mesmo aviso. O backend informa em toda resposta qual versão está
 * publicada (o cabeçalho que o {@code versaoInterceptorFn} lê), e o service worker avisa quando
 * terminou de baixar um build novo. A primeira é a confiável: não passa pelo CloudFront, e por
 * isso não depende de o manifesto do worker escapar do cache da borda — que é onde a detecção
 * anterior morria.</p>
 *
 * <p>O obrigatório tem precedência absoluta: quando ele bloqueia, este aviso não aparece. Somar
 * um convite a um bloqueio só daria ao usuário a impressão de que existe escolha.</p>
 */
@Injectable({ providedIn: 'root' })
export class AtualizacaoDisponivelService {

  private readonly snackbar = inject(MatSnackBar);
  private readonly updates = inject(SwUpdate);
  private readonly obrigatoria = inject(AtualizacaoObrigatoriaService);

  /** Aviso aberto agora: várias respostas chegam juntas e não podem empilhar snackbars. */
  private aberto = false;

  /**
   * Versão publicada, anunciada pelo backend em toda resposta da API.
   *
   * <p>Cabeçalho ausente ou versão igual/anterior à que está aberta não produzem nada — é o caso
   * normal, e o silêncio aqui é o comportamento correto.</p>
   */
  anunciar(versaoPublicada: string | null | undefined): void {
    const publicada = (versaoPublicada ?? '').trim();
    if (!publicada || compararVersoes(publicada, environment.version) <= 0) {
      return;
    }
    this.oferecer(publicada);
  }

  /**
   * O service worker terminou de baixar um build novo. Ele compara hashes, não versões, então não
   * há número a exibir — mas a oferta é a mesma, e reaproveitar este aviso evita duas interfaces
   * dizendo a mesma coisa de formas diferentes.
   */
  anunciarBuildNovo(): void {
    this.oferecer(null);
  }

  private oferecer(versaoPublicada: string | null): void {
    // Bloqueado pelo obrigatório: não há escolha a oferecer.
    if (this.obrigatoria.bloqueado() || this.aberto || this.adiado(versaoPublicada)) {
      return;
    }
    this.aberto = true;

    const dados: AtualizacaoDisponivelData = { versaoPublicada };
    this.snackbar.openFromComponent(AtualizacaoDisponivelSnackbarComponent, {
      data: dados,
      // Sem `duration`: o aviso espera. Some sozinho, e quem estava concentrado nunca o veria.
      duration: 0,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'fury-atualizacao-disponivel',
    }).afterDismissed().subscribe(resultado => {
      this.aberto = false;
      if (resultado.dismissedByAction) {
        this.recarregar();
        return;
      }
      this.adiar(versaoPublicada);
    });
  }

  /**
   * "Depois" cala o aviso por uma hora — ou até sair uma versão ainda mais nova, o que vier
   * primeiro. Sem isso, cada resposta da API traria o aviso de volta, e insistir é a forma mais
   * rápida de ensinar o usuário a ignorar.
   */
  private adiar(versaoPublicada: string | null): void {
    const adiamento: Adiamento = {
      versao: versaoPublicada ?? SEM_VERSAO,
      ate: Date.now() + SILENCIO_MS,
    };
    try {
      sessionStorage.setItem(CHAVE_ADIAMENTO, JSON.stringify(adiamento));
    } catch {
      // Navegação anônima ou storage bloqueado: sem persistir, o `aberto` ainda evita a repetição
      // enquanto o aviso está em tela. Perder o adiamento é melhor do que quebrar a tela.
    }
  }

  /** Adiada é só a versão que o usuário dispensou: uma mais nova volta a ser oferecida. */
  private adiado(versaoPublicada: string | null): boolean {
    const adiamento = this.leituraDoAdiamento();
    if (!adiamento || Date.now() >= adiamento.ate) {
      return false;
    }
    const versao = versaoPublicada ?? SEM_VERSAO;
    if (versao === SEM_VERSAO || adiamento.versao === SEM_VERSAO) {
      return adiamento.versao === versao;
    }
    return compararVersoes(versao, adiamento.versao) <= 0;
  }

  private leituraDoAdiamento(): Adiamento | null {
    try {
      const bruto = sessionStorage.getItem(CHAVE_ADIAMENTO);
      return bruto ? JSON.parse(bruto) as Adiamento : null;
    } catch {
      return null;
    }
  }

  /**
   * Recarrega de verdade — ver {@link recarregarComVersaoNova}. Um reload comum é atendido pelos
   * caches e traria de volta a mesma versão, com o aviso reaparecendo em seguida.
   *
   * <p>Só acontece por clique em "Atualizar agora". Recarregar sozinho custaria o formulário de
   * quem está no meio de um cadastro.</p>
   */
  private recarregar(): void {
    void recarregarComVersaoNova(this.updates);
  }
}
